import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AgendaTabs } from '../shell/AgendaTabs';
import { api } from '../../shared/api/client';
import type { MeetingSummary } from '../../shared/api/types';
import { Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { MeetingsList } from './MeetingsList';

type Target = { ministryId: string; cellId: string | null; name: string };
const formUrl = (t: Target) => `/app/ministerios/${t.ministryId}/reuniones/nueva?desde=reuniones${t.cellId ? `&celula=${t.cellId}` : ''}`;

/** Mis próximas reuniones: de mis ministerios y mis células; «Nueva reunión» para quien puede crearlas. */
export function MeetingsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const data = useAsync(
    (signal) => (churchId ? api.get<{ items: MeetingSummary[]; canCreateIn?: Target[] }>(`/organization/churches/${churchId}/meetings`, signal) : Promise.resolve({ items: [], canCreateIn: [] })),
    [churchId],
  );
  const navigate = useNavigate();
  const [choosing, setChoosing] = useState(false);
  const targets = data.status === 'ready' ? (data.data.canCreateIn ?? []) : [];
  return (
    <div className="page">
      <AgendaTabs />
      <header className="page__header page__header--actions">
        <div>
          <h1>Reuniones</h1>
          <p className="muted">De tus ministerios y tus células.</p>
        </div>
        {targets.length > 0 && !choosing ? (
          <Button onClick={() => (targets.length === 1 ? navigate(formUrl(targets[0]!)) : setChoosing(true))}>Nueva reunión</Button>
        ) : null}
      </header>
      {choosing ? (
        <Card title="Nueva reunión">
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const t = targets[Number(new FormData(e.currentTarget).get('target'))];
              if (t) navigate(formUrl(t));
            }}
          >
            <div className="field">
              <label htmlFor="meeting-target">¿De qué ministerio o célula?</label>
              <select id="meeting-target" name="target" defaultValue="0">
                {targets.map((t, i) => (
                  <option key={`${t.ministryId}-${t.cellId ?? ''}`} value={i}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="actions">
              <Button type="submit">Continuar</Button>
              <Button type="button" variant="ghost" onClick={() => setChoosing(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
      {!churchId ? <EmptyState title="Elige primero una iglesia en el selector de arriba." /> : null}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && churchId ? (
        <Card>{data.data.items.length === 0 ? <EmptyState title="No tienes reuniones próximas." /> : <MeetingsList items={data.data.items} showMinistry />}</Card>
      ) : null}
    </div>
  );
}
